import {
  INCREMENTAL_VALUE_SEPARATOR,
  Override,
  RemoteConfig,
} from '@ca/common/types'
import { getRemoteConfig } from 'hooks/useRemoteConfigStore'

import { VariableValue, VariableValues } from '@ca/common/types'

export { VariableValue, VariableValues }

const INCREMENTAL_VARIABLES = ['coatings']

const isIncrementalVariable = (
  variable: string,
  /**
   * Prefer to pass config in loops etc
   */
  config?: RemoteConfig | null
) => {
  const remoteConfig = config ?? getRemoteConfig()

  const cfg = remoteConfig?.variables?.[variable]

  if (cfg?.incremental) {
    return true
  }

  return INCREMENTAL_VARIABLES.includes(variable)
}

const isValidOverride = (value: Partial<Override>): value is Override => {
  return value.variable != undefined
}

const mergeValue = (
  variable: string,
  a: VariableValue,
  b: VariableValue,
  config: RemoteConfig | null
) => {
  const isIncremental = isIncrementalVariable(variable, config)
  if (isIncremental && a) {
    if (typeof b === 'undefined') {
      return a
    }
    return `${a}${INCREMENTAL_VALUE_SEPARATOR}${b}`
  }
  return b
}

export const overridesArrayToValues = (
  overrides: [string, string | null][] | undefined
) => {
  if (!overrides) {
    return {}
  }

  const result: VariableValues = {}

  const config = getRemoteConfig()

  overrides.forEach(([variable, value]) => {
    result[variable] = mergeValue(variable, result[variable], value, config)
  })

  return result
}

export const mergeValues = (...args: (VariableValues | null)[]) => {
  const [source, ...rest] = args
  const config = getRemoteConfig()
  const result = {
    ...source,
  }
  rest.forEach((values) => {
    if (!values) {
      return
    }
    Object.entries(values).forEach(([variable, value]) => {
      result[variable] = mergeValue(variable, result[variable], value, config)
    })
  })
  return result
}

export const getOverridesDictionary = (overrides?: Partial<Override>[]) => {
  if (!overrides) {
    return {}
  }
  const entries = overrides
    .filter(isValidOverride)
    .map((o) => [o.variable, o.value ?? ''] satisfies [string, string])
  const res = overridesArrayToValues(entries)
  return res
}

const fromArrayToTuples = (values: Partial<Override>[]) => {
  const valid = values.filter(isValidOverride)

  return valid.map(
    (v) => [v.variable, v.value ?? ''] satisfies [string, string]
  )
}

export const VariablesUtils = {
  value: {
    merge: mergeValue,
    isIncremental: isIncrementalVariable,
  },
  merge: mergeValues,
  fromArray: overridesArrayToValues,
  fromOverrides: getOverridesDictionary,
  toStateArray: fromArrayToTuples,
}
